import styles from "components/sections/search/AllSearchResult.module.scss";
import {Axios} from "api";
import {trackPixel} from "api/pixel";
import {productScrapContextRef} from "context/ProductScrapContext";
import {captureException} from "@sentry/nextjs";
import {useContext, useEffect, useMemo, useState} from "react";
import BrandSearchItemCard from "components/cards/search/BrandSearchItemCard";
import HorizontalProducts from "components/layout/store/products/HorizontalProducts";
import HorizontalPosts from "components/layout/palette/HorizontalPosts";
import SellerShopSearchItemCard from "components/cards/search/SellerShopSearchItemCard";
import SearchHome from "components/sections/search/SearchHome";
import {NoAllSearchResult} from "components/sections/search/NoSearchResult";
import {KEY_BRAND, KEY_PALETTE, KEY_SELLER_SHOP, KEY_STORE} from "components/sections/search/SearchResult";
import LoadingSearchResult, {SearchResultError} from "components/sections/search/LoadingSearchResult";
import PostListContext from "context/PostListContext";
import {OverallStoreSection} from "components/sections/search/store/OverallStoreSection";
import {SectionTitle} from "components/sections/search/SectionTitle";
import SimpleSearchPostListCard from "components/cards/SimpleSearchPostListCard";
import {numberWithComma} from "common/utils";
import {useRouter} from "next/router";
import {eventsRoute} from "common/const";
import {EventListCard} from "pages/events";
import useTranslation from "next-translate/useTranslation";


const BrandSection = (props) => {
    const {brands, setActiveTab, onItemClick, query} = props;
    const {t} = useTranslation('components-sections-AllSearchResult');

    const handleShowMoreClick = () => {
        typeof mixpanel !== 'undefined' && mixpanel.track(
            'AllSearchResultBrandSectionShowMoreClick',
        );
        setActiveTab(KEY_BRAND);
    };

    const onBrandClick = (brand, itemIdx) => {
        typeof mixpanel !== 'undefined' && mixpanel.track(
            'AllSearchResultBrandSectionItemClick',
            {
                brandName: brand.name,
                itemIdx: itemIdx,
            }
        );
        onItemClick();
    };

    return (
        <div className={styles.sectionContainer}>
            <SectionTitle title={<span className={styles.displayTitle}>{t('SearchType.Brand')}</span>} onClick={handleShowMoreClick}/>
            <div className={styles.brandListContainer}>
                {
                    brands.slice(0, 3).map((e, index) => {
                        return (
                            <BrandSearchItemCard
                                brand={e} key={index}
                                onClick={() => onBrandClick(e, index)}
                                highlightKeyword={query}
                            />
                        )
                    })
                }
            </div>
        </div>
    );
};

const StoreSection = (props) => {
    const {storeResult, setActiveTab, onItemClick} = props;
    const {t} = useTranslation('components-sections-AllSearchResult');

    const hasMore = storeResult.products.length > SHOW_MORE_LIMIT;

    const handleShowMoreClick = () => {
        typeof mixpanel !== 'undefined' && mixpanel.track(
            'AllSearchResultStoreSectionShowMoreClick',
        );
        setActiveTab(KEY_STORE);
    };

    const onProductClick = (product, itemIdx) => {
        typeof mixpanel !== 'undefined' && mixpanel.track(
            'AllSearchResultStoreSectionItemClick',
            {
                productName: product.name,
                productId: product.id,
                itemIdx: itemIdx,
            }
        );
        onItemClick();
    };

    return (
        <div className={styles.sectionContainer}>
            <SectionTitle
                title={<span className={styles.displayTitle}>{t('SearchType.Store')}<span className={styles.count}>({numberWithComma(storeResult.count)})</span></span>}
                onClick={handleShowMoreClick}
            />
            <div className={styles.storeListContainer}>
                <HorizontalProducts
                    products={storeResult.products}
                    onProductClick={onProductClick}
                    width={135} imageRatio={'100%'}
                    productNameMaxLine={2}
                    useBestCouponDiscount={true}
                    miniScrapIcon
                    onShowMoreClick={hasMore ? handleShowMoreClick : undefined}
                />
            </div>
        </div>
    )
};


const PaletteSection = (props) => {
    const {paletteResult, setActiveTab, onItemClick, query} = props;
    const {t} = useTranslation('components-sections-AllSearchResult');

    const hasMore = paletteResult.posts.length > SHOW_MORE_LIMIT;

    const handleShowMoreClick = () => {
        typeof mixpanel !== 'undefined' && mixpanel.track(
            'AllSearchResultPaletteSectionShowMoreClick',
        );
        setActiveTab(KEY_PALETTE);
    };

    const onPostClick = (post, itemIdx) => {
        typeof mixpanel !== 'undefined' && mixpanel.track(
            'AllSearchResultPaletteSectionItemClick',
            {
                postId: post.id,
                itemIdx: itemIdx,
            }
        );
        onItemClick();
    };

    const renderItem = (post, index) => {
        return (
            <SimpleSearchPostListCard
                post={post}
                miniIcon={true}
                onPostClick={() => onPostClick(post, index)}
                imageRatio={'130%'}
                showInlineProduct
                highlightKeyword={query}
                mediaIconPos={{
                    size: 24,
                    left: 5,
                    top: 5,
                }}
            />
        )
    }

    return (
        <div className={styles.sectionContainer}>
            <SectionTitle
                title={<span className={styles.displayTitle}>{t('SearchType.Palette')}<span className={styles.count}>({numberWithComma(paletteResult.count)})</span></span>}
                onClick={handleShowMoreClick}
            />
            <div className={styles.paletteListContainer}>
                <HorizontalPosts
                    posts={paletteResult.full_posts} imageRatio={'120%'}
                    width={140} showUser hideScrapButton
                    onPostClick={onPostClick}
                    onShowMoreClick={hasMore ? handleShowMoreClick : undefined}
                    // hideTextAndProduct
                    showInlineProduct
                    showText
                    renderItem={renderItem}
                />
            </div>
        </div>
    )
};


const EventSection = (props) => {
    const {eventResult, onItemClick, query} = props;
    const {t} = useTranslation('components-sections-AllSearchResult');

    const router = useRouter();

    const handleShowMoreClick = () => {
        typeof mixpanel !== 'undefined' && mixpanel.track(
            'AllSearchResultEventSectionShowMoreClick',
        );
        router.push(eventsRoute);
    };

    const onEventClick = (event, itemIdx) => {
        typeof mixpanel !== 'undefined' && mixpanel.track(
            'AllSearchResultEventSectionItemClick',
            {
                itemIdx: itemIdx,
            }
        );
        onItemClick();
    };

    const title = <span className={styles.displayTitle}>
        {t('SearchType.Event')}
        <span className={styles.count}>({numberWithComma(eventResult.count)})</span>
    </span>

    return (
        <div className={styles.sectionContainer}>
            <SectionTitle title={title} onClick={handleShowMoreClick}/>
            <div className={styles.brandListContainer}>
                {
                    eventResult.events.slice(0, 3).map((e, index) => {
                        return (
                            <EventListCard key={`event-${e.id}`} event={e} onClick={onEventClick} highlightKeyword={query} marginTop={0}/>
                        )
                    })
                }
            </div>
        </div>
    )
};


const SellerShopSection = (props) => {
    const {sellerShopResult, setActiveTab, onItemClick, query} = props;
    const {t} = useTranslation('components-sections-AllSearchResult');

    const handleShowMoreClick = () => {
        typeof mixpanel !== 'undefined' && mixpanel.track(
            'AllSearchResultSellerShopSectionShowMoreClick',
        );
        setActiveTab(KEY_SELLER_SHOP);
    };

    const onShopClick = (shop, itemIdx) => {
        typeof mixpanel !== 'undefined' && mixpanel.track(
            'AllSearchResultSellerShopSectionItemClick',
            {
                itemIdx: itemIdx,
            }
        );
        onItemClick();
    };

    const title = useMemo(() => {
        return t('SearchType.SellerShop')
    }, []);

    return (
        <div className={styles.sectionContainer}>
            <SectionTitle title={title} onClick={handleShowMoreClick}/>
            <div className={styles.brandListContainer}>
                {
                    sellerShopResult.shops.slice(0, 3).map((e, index) => {
                        return (
                            <SellerShopSearchItemCard
                                sellerShop={e} key={index}
                                onItemClick={() => onShopClick(e, index)}
                                highlightKeyword={query}
                            />
                        )
                    })
                }
            </div>
        </div>
    )
};

const SHOW_MORE_LIMIT = 10;

const BRAND_URL = 'v2/search/brand';
const PRODUCT_URL = 'v2/search/product';
const POST_URL = 'v2/search/post';
const SELLER_URL = 'v2/search/seller';
const EVENT_URL = 'v2/search/event';

const BITMASK_BRAND = (1 << 0);
const BITMASK_PRODUCT = (1 << 1);
const BITMASK_POST = (1 << 2);
const BITMASK_SELLER = (1 << 3);
const BITMASK_EVENT = (1 << 4);

const SEARCH_INFOS = {
    brand: {
        url: BRAND_URL,
        bitmask: BITMASK_BRAND,
    },
    product: {
        url: PRODUCT_URL,
        bitmask: BITMASK_PRODUCT,
    },
    post: {
        url: POST_URL,
        bitmask: BITMASK_POST,
    },
    seller: {
        url: SELLER_URL,
        bitmask: BITMASK_SELLER,
    },
    event: {
        url: EVENT_URL,
        bitmask: BITMASK_EVENT,
    },
};


const DEFAULT_DATA = {
    brand: [],
    product: {
        overall_store_search_result: {
            filtered_product_list: [],
        },
        default_store_search_result: {
            count: 0,
            products: [],
            scrapped_post_ids: [],
            cursor: null,
        },
    },
    post: {
        counts: 0,
        posts: [],
        scrapped_post_ids: [],
        cursor: null,
        full_posts: [],
    },
    seller: [],
    event: {
        count: 0,
        events: [],
    },
    LOADED: 0,
};

const MINIMUM_VISIBLE_THRESHOLD = 2;

const AllSearchResult = (props) => {
    const {query, setQuery, setActiveTab} = props;
    const [error, setError] = useState(0);
    const [data, setData] = useState(DEFAULT_DATA);

    const {reportedPostIds} = useContext(PostListContext);

    useEffect(() => {
        fetchSearchResult();
    }, [query]);

    useEffect(() => {
        typeof mixpanel !== 'undefined' && mixpanel.track(
            'ViewAllSearchResult',
            {query},
        );
    }, []);

    const fetchSearchResult = () => {
        setError(0);
        setData(DEFAULT_DATA);

        if (!Boolean(query) || query.trim().length === 0) {
            return;
        }

        for (const key in SEARCH_INFOS) {
            getSearchResult(SEARCH_INFOS[key]);
        }
    }

    const getSearchResult = (search_info) => {
        const {url, bitmask} = search_info;

        Axios.get(url, {params: {query: query}})
            .then(res => {
                if (res.status < 400) {
                    const type = res.data.type

                    setData(prev => {

                        let newResult = {
                            ...prev,
                            [type]: res.data.result,
                        }

                        if (type === 'post') {
                            newResult = updatePaletteResult(newResult);
                        } else if (type === 'product') {
                            productScrapContextRef.current.addProductIds(res?.data?.result?.default_store_search_result?.scrapped_product_ids);
                            const trackPixelData = {
                                content_ids: res?.data?.result?.default_store_search_result?.products.map(product => product.id),
                                search_string: query,
                            }
                            trackPixel('Search', trackPixelData);
                        }

                        return newResult;
                    })
                } else {
                    setError(prev => {
                        prev |= bitmask;
                        return prev;
                    });
                }
            }).catch(e => {
            captureException(e);
            setError(prev => {
                prev |= bitmask;
                return prev;
            });
        }).finally(() => {
            setData(prev => {
                return {
                    ...prev,
                    LOADED: prev.LOADED | bitmask,
                }
            })
        });
    }

    const updatePaletteResult = (result) => {
        if (result?.post) {
            const paletteResult = result.post;
            const newPaletteResultPosts = paletteResult.posts.filter((post) => !reportedPostIds.has(post.id));
            return {
                ...result,
                post: {
                    ...paletteResult,
                    posts: newPaletteResultPosts,
                }
            };
        }
        return result;
    }

    useEffect(() => {
        setData(prev => updatePaletteResult(prev));
    }, [reportedPostIds]);

    const loadedStep = useMemo(() => {
        const binaryString = data.LOADED.toString(2);
        const step = binaryString.length - binaryString.indexOf('0') - 1;
        console.log('binaryString', binaryString, step);

        if (step < MINIMUM_VISIBLE_THRESHOLD) {
            return 0;
        }

        return step;
    }, [data?.LOADED]);

    console.log('loadedStep', loadedStep);

    const noResults = useMemo(() => {
        const ALL_BITMASK = BITMASK_BRAND | BITMASK_PRODUCT | BITMASK_POST | BITMASK_SELLER | BITMASK_EVENT;
        return (
            data?.LOADED === ALL_BITMASK &&
            !Boolean(data.brand?.length) &&
            !Boolean(data.product?.overall_store_search_result?.filtered_product_list?.length) &&
            !Boolean(data.product?.default_store_search_result?.products?.length) &&
            !Boolean(data.post?.posts.length) &&
            !Boolean(data.seller?.shops?.length) &&
            !Boolean(data.event?.events?.length)
        )
    }, [data?.LOADED]);

    const handleRefresh = () => {
        fetchSearchResult();
    };

    const onItemClick = (item, section) => {
        typeof mixpanel !== 'undefined' && mixpanel.track(
            'AllSearchResultItemClick',
            {
                query,
                section,
            }
        );
        if (props.onItemClick) {
            props.onItemClick(item);
        }
    };

    const hasMatchingBrand = data?.product?.overall_store_search_result?.filtered_product_list?.some(
        x => x.filter_info_list && x.filter_info_list[0].filter_type === 'brand'
    );

    return (
        <div className={styles.content}>
            {
                query && query.trim().length > 0
                    ?
                    (
                        error > 0
                            ? <SearchResultError handleRefresh={handleRefresh}/>
                            :
                            (
                                noResults
                                    ? <NoAllSearchResult query={query}/>
                                    :
                                    <div className={styles.root}>
                                        {
                                            loadedStep > 0 && data.brand?.length > 0 &&
                                            <BrandSection
                                                brands={data.brand}
                                                setActiveTab={setActiveTab}
                                                onItemClick={(item) => onItemClick(item, 'brand')}
                                                query={query}
                                            />
                                        }
                                        {
                                            loadedStep > 1 && data.product?.overall_store_search_result?.filtered_product_list?.length > 0 &&
                                            <OverallStoreSection
                                                overallStoreResult={data.product.overall_store_search_result}
                                                setActiveTab={setActiveTab}
                                                onItemClick={(item) => onItemClick(item, 'store_overall')}
                                            />
                                        }
                                        {
                                            loadedStep > 1 && data.product?.default_store_search_result?.products.length > 0 && !hasMatchingBrand &&
                                            <StoreSection
                                                storeResult={data.product.default_store_search_result}
                                                setActiveTab={setActiveTab}
                                                onItemClick={(item) => onItemClick(item, 'store')}
                                            />
                                        }
                                        {
                                            loadedStep > 2 && data.post?.posts?.length > 0 &&
                                            <PaletteSection
                                                paletteResult={data.post}
                                                setActiveTab={setActiveTab}
                                                onItemClick={(item) => onItemClick(item, 'palette')}
                                                query={query}
                                            />
                                        }
                                        {
                                            loadedStep > 3 && data.seller?.shops?.length > 0 &&
                                            <SellerShopSection
                                                sellerShopResult={data.seller}
                                                setActiveTab={setActiveTab}
                                                onItemClick={(item) => onItemClick(item, 'seller-shop')}
                                                query={query}
                                            />
                                        }
                                        {
                                            loadedStep > 4 && data.event?.events?.length > 0 &&
                                            <EventSection
                                                eventResult={data.event}
                                                setActiveTab={setActiveTab}
                                                onItemClick={(item) => onItemClick(item, 'event')}
                                                query={query}
                                            />
                                        }
                                        {
                                            !(loadedStep > 4) && <LoadingSearchResult/>
                                        }
                                        <div className={styles.bottomPadding}/>
                                    </div>
                            )
                    )
                    : <div className={styles.root}>
                        <SearchHome setSearchText={setQuery} onItemClick={onItemClick}/>
                    </div>
            }
        </div>
    )
};

export default AllSearchResult;
